import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Clock, ArrowRight, Share2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function StoryShare() {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [notFound, setNotFound] = useState(false);

    // The crawler-friendly URL — hitting this returns OG metadata and 302s humans to /story/:id
    const shareUrl = `${BACKEND}/api/share/story/${id}`;
    const canonical = typeof window !== "undefined" ? window.location.href : "";

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/feed/${id}`);
                setItem(data);
            } catch {
                setNotFound(true);
            }
        })();
    }, [id]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Lien de partage copié", {
                description: "Les aperçus (OG) se chargent sur Discord, LinkedIn, etc.",
            });
        } catch {
            toast.error("Impossible de copier le lien");
        }
    };

    const nativeShare = async () => {
        if (!navigator.share) return copy();
        try {
            await navigator.share({ url: shareUrl, title: item?.headline });
        } catch {
            // user cancelled
        }
    };

    if (notFound)
        return (
            <div className="mx-auto max-w-[720px] px-6 py-24 text-center">
                <h1 className="font-display text-4xl uppercase text-white">
                    Histoire introuvable
                </h1>
                <Link
                    to="/feed"
                    className="mt-6 inline-block border border-white/20 px-4 py-2 text-xs uppercase tracking-widest text-white hover:border-[#FF2E2E]"
                >
                    Retour au fil
                </Link>
            </div>
        );

    if (!item)
        return (
            <div className="mx-auto max-w-[720px] px-6 py-24">
                <div className="h-96 animate-pulse border border-white/10 bg-[#121212]" />
            </div>
        );

    const description = `${item.student_name} · ${item.duration_label}. Coaché·e par ${item.coach_name}.`;

    return (
        <div className="mx-auto max-w-[720px] px-6 py-12">
            <Helmet>
                <title>{`${item.headline} — EarnIt`}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonical} />
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content="EarnIt" />
                <meta property="og:title" content={item.headline} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content={item.media_url} />
                <meta property="og:url" content={canonical} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={item.headline} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={item.media_url} />
            </Helmet>

            <Link
                to="/feed"
                className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 hover:text-white"
            >
                ← Fil de progression
            </Link>

            <article
                data-testid="story-share-article"
                className="mt-6 border border-white/10 bg-[#121212]"
            >
                <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                        src={item.media_url}
                        alt={item.headline}
                        className="h-full w-full object-cover"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                    <span
                        className={`absolute left-4 top-4 font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1 ${
                            item.coach_category === "physical"
                                ? "bg-[#FF2E2E] text-white"
                                : "border border-white/40 bg-black/60 text-white"
                        }`}
                    >
                        {item.coach_category === "physical" ? "Corps" : "Esprit"}
                    </span>
                    <div className="absolute inset-x-0 bottom-0 space-y-3 p-6">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-300">
                            {item.student_name} ·{" "}
                            <Clock className="inline h-3 w-3" /> {item.duration_label}
                        </div>
                        <h1 className="font-display text-3xl uppercase leading-tight text-white sm:text-4xl">
                            {item.headline}
                        </h1>
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/10 p-5">
                    <Link
                        to={`/coach/${item.coach_id}`}
                        className="inline-flex items-center gap-3 text-white hover:text-[#FF2E2E]"
                    >
                        <img
                            src={item.coach_photo}
                            alt=""
                            className="h-10 w-10 object-cover"
                        />
                        <div>
                            <div className="font-display text-lg uppercase leading-none">
                                {item.coach_name}
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                {item.coach_city}
                            </div>
                        </div>
                        <ArrowRight className="h-4 w-4" />
                    </Link>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="story-copy-link"
                            onClick={copy}
                            className="inline-flex items-center gap-2 border border-[#FF2E2E] bg-[#FF2E2E]/10 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-[#FF2E2E] hover:bg-[#FF2E2E] hover:text-white"
                        >
                            <Copy className="h-3.5 w-3.5" />
                            Copier le lien
                        </button>
                        <button
                            data-testid="story-native-share"
                            onClick={nativeShare}
                            className="inline-flex items-center gap-2 border border-white/20 bg-black px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:border-[#FF2E2E]"
                        >
                            <Share2 className="h-3.5 w-3.5" />
                            Partager
                        </button>
                    </div>
                </div>
            </article>

            <p className="mt-6 border border-dashed border-white/10 p-4 text-center text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Lien partageable avec aperçu — sans jamais passer par un réseau social.
            </p>
        </div>
    );
}
